import { atom } from "jotai";

// Mirrors order-handler SharedRecommendedDesign (interior_project.ts). Each
// recommendation carries its AI mockup (design composited onto the wall photo),
// generated at share time. ai_mockup_signed_url is short-lived; null until ready.
export interface SharedRecommendedDesign {
    id: number;
    design_id: number;
    rank: number;
    title: string | null;
    thumbnail_url: string | null;
    mockup_status: 'PENDING' | 'GENERATED' | 'FAILED';
    ai_mockup_signed_url: string | null;
}

// Mirrors order-handler SharedWallSelectionView — the homeowner's saved pick.
export interface SharedWallSelectionView {
    id: number;
    design_id: number;
    mockup_status: 'PENDING' | 'GENERATED' | 'FAILED';
    ai_mockup_signed_url: string | null;
}

// Mirrors order-handler SharedWallView. One design is chosen per wall, so
// selected_design_ids holds at most one id.
export interface SharedWall {
    id: number;
    name: string;
    wall_width: number | null;
    wall_height: number | null;
    material_id: number | null;
    wall_photo_signed_url: string | null;
    recommended_designs: SharedRecommendedDesign[];
    selected_design_ids: number[];
    // Saved selections with per-design mockup status + signed URL (one per wall).
    selections: SharedWallSelectionView[];
}

export type SharedProjectStatus = 'DRAFT' | 'SHARED' | 'SELECTIONS_COMPLETE' | 'ORDERED';

// Mirrors order-handler SharedBrandingView — the designer's public branding.
// Always present: the server resolves per-field defaults, so the page never
// renders unstyled. Carries no brand ids.
export interface SharedBranding {
    logo_url: string | null;
    cover_image_url: string | null;
    primary_color: string;
    accent_color: string;
    font_key: string;
    welcome_headline: string | null;
    welcome_body: string | null;
    contact_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    contact_website: string | null;
}

// Mirrors order-handler SharedProjectView
export interface SharedProjectView {
    id: number;
    client_name: string;
    status: SharedProjectStatus;
    walls: SharedWall[];
    branding: SharedBranding;
}

export type ShareLinkError = 'INVALID' | 'EXPIRED' | 'GENERIC';

export type WallSaveState = 'idle' | 'saving' | 'saved' | 'error';

// The shared project as loaded via the magic-link token (null until fetched).
export const sharedProjectAtom = atom<SharedProjectView | null>(null);

// Fatal link-level error (invalid / expired / generic failure).
export const shareErrorAtom = atom<ShareLinkError | null>(null);

// Initial load spinner state.
export const shareLoadingAtom = atom<boolean>(false);

// Local (unconfirmed) pick per wall — value is the catalog design_id (at most
// one per wall). Stored as an array for a stable save-payload shape.
export const localSelectionsAtom = atom<Record<number, number[]>>({});

// Last set of picks known to be persisted server-side, per wall.
export const lastSavedSelectionsAtom = atom<Record<number, number[]>>({});

// Per-wall auto-save status.
export const wallSaveStateAtom = atom<Record<number, WallSaveState>>({});

// Confirm POST in flight.
export const confirmingAtom = atom<boolean>(false);

// Non-fatal notice (e.g. designer changed recommendations mid-session).
export const shareNoticeAtom = atom<string | null>(null);
